import type { HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { springs } from "@samklang/design-tokens";
import { cn } from "../lib/cn";

/**
 * Headspace-inspired cards: soft, spacious, rounded, light elevation.
 * Padding is intentionally generous so content never feels edge-stuck.
 */
const cardVariants = cva(
  [
    // --radius-card is 24px, a step of its own between --radius-md (20px) and
    // --radius-lg (28px), and the shape the mockups draw every card with.
    "rounded-card text-foreground",
    "border border-border/55",
    "bg-surface-raised/90 backdrop-blur-sm",
    "shadow-card",
  ].join(" "),
  {
    variants: {
      padding: {
        none: "p-0",
        sm: "p-5",
        md: "p-6 sm:p-7",
        lg: "p-7 sm:p-9",
      },
      elevation: {
        flat: "shadow-none border-border/40 bg-surface-raised/70",
        raised: "",
        floating: "shadow-float border-border/45",
      },
      interactive: {
        true: "cursor-pointer",
        false: "",
      },
    },
    defaultVariants: {
      padding: "md",
      elevation: "raised",
      interactive: false,
    },
  }
);

export type CardProps = {
  children?: ReactNode;
  className?: string;
  padding?: VariantProps<typeof cardVariants>["padding"];
  elevation?: VariantProps<typeof cardVariants>["elevation"];
  interactive?: boolean;
  asButton?: boolean;
  id?: string;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  onClick?: (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
};

export function Card(
  {
    className,
    padding = "md",
    elevation = "raised",
    interactive = false,
    asButton = false,
    children,
    onClick,
    id,
    "aria-labelledby": ariaLabelledby,
    "aria-label": ariaLabel,
  }: CardProps
) {
  const reducedMotion = useReducedMotion();
  const isInteractive = interactive || asButton || Boolean(onClick);

  if (isInteractive) {
    return (
      <motion.button
        type="button"
        id={id}
        role="button"
        tabIndex={0}
        aria-labelledby={ariaLabelledby}
        aria-label={ariaLabel}
        className={cn(
          cardVariants({
            padding,
            elevation,
            interactive: true,
          }),
          "block min-h-11 w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          className
        )}
        {...(reducedMotion
          ? {}
          : {
              whileTap: { scale: 0.988 },
              whileHover: { y: -2, transition: springs.soft },
            })}
        onClick={onClick}
        onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
          if (!onClick) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick(event);
          }
        }}
      >
        {children}
      </motion.button>
    );
  }

  return (
    <div
      id={id}
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      className={cn(
        cardVariants({ padding, elevation, interactive: false }),
        className
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mb-5 space-y-2", className)} {...props} />;
}

export function CardTitle({
  className,
  as: Tag = "h3",
  ...props
}: HTMLAttributes<HTMLHeadingElement> & { as?: "h2" | "h3" | "h4" | "p" }) {
  return (
    <Tag
      className={cn(
        "font-display text-xl font-semibold tracking-tight text-foreground",
        className
      )}
      {...props}
    />
  );
}

export function CardDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn("text-body-sm leading-relaxed text-foreground-muted", className)}
      {...props}
    />
  );
}

export { cardVariants };
